import { useState, useCallback, useEffect } from "react";
import {
  Dialog,
  DialogPanel,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import { Fragment } from "react";
import { X, CircleDollarSign } from "lucide-react";
import { MultiFileUpload } from "./MultiFileUpload";
import { InputText } from "@/shared/components/form/InputText";
import useTranslations from "@/shared/lang/useTranslations";
import { PAYMENT_SOURCE_OPTIONS } from "../constants/paymentSourceOptions";
import { cn } from "@/lib/utils";
import { isCryptoWalletCurrency } from "../../confirmations/constants/currencies.config";

type ConfirmPaidModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (attachments?: File[], urlHas?: string, paymentSource?: string) => void;
  isLoading?: boolean;
  requireComprobante?: boolean;
  currency?: string;
};

export const ConfirmPaidModal = ({
  isOpen,
  onClose,
  onConfirm,
  isLoading = false,
  requireComprobante = true,
  currency,
}: ConfirmPaidModalProps) => {
  const { t } = useTranslations("payments");
  const [paymentSource, setPaymentSource] = useState("");
  const [paymentSourceError, setPaymentSourceError] = useState<string | null>(null);
  const [comprobantes, setComprobantes] = useState<File[]>([]);
  const [comprobanteError, setComprobanteError] = useState<string | null>(null);
  const [urlHas, setUrlHas] = useState("");
  const [urlHasError, setUrlHasError] = useState<string | null>(null);

  const showUrlHasField = isCryptoWalletCurrency(currency ?? "");

  const resetForm = useCallback(() => {
    setPaymentSource("");
    setPaymentSourceError(null);
    setComprobantes([]);
    setComprobanteError(null);
    setUrlHas("");
    setUrlHasError(null);
  }, []);

  useEffect(() => {
    if (!isOpen) resetForm();
  }, [isOpen, resetForm]);

  const handleConfirmClick = useCallback(() => {
    const source = paymentSource.trim();
    if (!source) {
      setPaymentSourceError(t("modal_paid_payment_source_required"));
      return;
    }
    setPaymentSourceError(null);

    // Para stablecoin (USDT/USDC): solo url_has es obligatorio; comprobante opcional.
    // Para otras monedas: comprobante obligatorio.
    const comprobanteRequired = requireComprobante && !showUrlHasField;

    if (comprobanteRequired) {
      if (comprobantes.length === 0) {
        setComprobanteError(t("modal_paid_comprobante_required"));
        return;
      }
      setComprobanteError(null);
    } else {
      setComprobanteError(null);
    }

    if (showUrlHasField) {
      const value = urlHas?.trim() ?? "";
      if (!value) {
        setUrlHasError(t("modal_paid_url_has_required"));
        return;
      }
      setUrlHasError(null);
    }

    onConfirm(
      comprobantes.length > 0 ? comprobantes : undefined,
      showUrlHasField ? urlHas.trim() : undefined,
      source
    );
  }, [requireComprobante, comprobantes, paymentSource, onConfirm, showUrlHasField, urlHas, t]);

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={() => {}}>
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div
            className="fixed inset-0 bg-black/40 backdrop-blur-sm"
            onClick={onClose}
            aria-hidden
          />
        </TransitionChild>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <DialogPanel className="w-full max-w-[560px] rounded-lg shadow-2xl overflow-hidden border border-gray-100 dark:border-zinc-800 bg-white dark:bg-zinc-900 transform transition-all">
                <div className="flex items-center justify-between px-8 pt-8 pb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-full bg-green-500/10">
                      <CircleDollarSign className="h-6 w-6 text-green-500 dark:text-green-400" />
                    </div>
                    <h2 className="text-gray-900 dark:text-white text-2xl font-bold tracking-tight">
                      {t("modal_paid_title")}
                    </h2>
                  </div>
                  <button
                    type="button"
                    onClick={onClose}
                    disabled={isLoading}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-white transition-colors p-1"
                    aria-label={t("modal_paid_close_aria")}
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>

                <div className="px-8 py-2">
                  <p className="text-gray-600 dark:text-zinc-400 text-base leading-relaxed">
                    {t("modal_paid_description")}
                  </p>
                </div>

                <div className="px-8 py-4 space-y-2">
                  <label
                    htmlFor="confirm-paid-payment-source"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                  >
                    {t("modal_paid_payment_source_label")}{" "}
                    <span className="text-red-500 dark:text-red-400" aria-hidden>*</span>
                  </label>
                  <select
                    id="confirm-paid-payment-source"
                    value={paymentSource}
                    onChange={(e) => {
                      setPaymentSource(e.target.value);
                      if (paymentSourceError) setPaymentSourceError(null);
                    }}
                    disabled={isLoading}
                    required
                    aria-required="true"
                    aria-invalid={!!paymentSourceError}
                    aria-describedby={paymentSourceError ? "confirm-paid-source-error" : undefined}
                    className={cn(
                      "w-full rounded-lg border bg-white dark:bg-zinc-800 text-gray-900 dark:text-white text-base",
                      "px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent",
                      "disabled:opacity-50 disabled:cursor-not-allowed",
                      paymentSourceError
                        ? "border-red-500 dark:border-red-400"
                        : "border-gray-300 dark:border-zinc-600"
                    )}
                  >
                    <option value="">{t("modal_paid_payment_source_placeholder")}</option>
                    {PAYMENT_SOURCE_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                  {paymentSourceError && (
                    <p
                      id="confirm-paid-source-error"
                      className="text-sm font-medium text-red-500 dark:text-red-400"
                      role="alert"
                    >
                      {paymentSourceError}
                    </p>
                  )}
                </div>

                {(requireComprobante || showUrlHasField) && (
                  <div className="px-8 py-4">
                    <MultiFileUpload
                      value={comprobantes}
                      onChange={setComprobantes}
                      label={showUrlHasField ? t("modal_paid_comprobante_optional_label") : t("modal_paid_comprobante_label")}
                      description={
                        showUrlHasField
                          ? t("modal_paid_comprobante_optional_description")
                          : t("modal_paid_comprobante_description")
                      }
                      maxSizeMB={10}
                      disabled={isLoading}
                      error={comprobanteError ?? undefined}
                      inputId="confirm-paid-comprobante"
                    />
                  </div>
                )}

                {showUrlHasField && (
                  <div className="px-8 py-2">
                    <InputText
                      label={t("modal_paid_url_has_label")}
                      value={urlHas}
                      onChange={(e) => {
                        setUrlHas(e.target.value);
                        if (urlHasError) setUrlHasError(null);
                      }}
                      placeholder={t("modal_paid_url_has_placeholder")}
                      disabled={isLoading}
                      errorMessage={urlHasError ?? undefined}
                      id="confirm-paid-url-has"
                      maxWidth="max-w-full"
                    />
                  </div>
                )}

                <div className="px-8 py-8 flex items-center justify-end gap-3">
                  <button
                    type="button"
                    onClick={onClose}
                    disabled={isLoading}
                    className="min-w-[100px] h-12 px-6 rounded-full bg-gray-100 dark:bg-zinc-800 text-gray-700 dark:text-white text-base font-bold transition-all hover:bg-gray-200 dark:hover:bg-zinc-700 disabled:opacity-50"
                  >
                    {t("modal_paid_back")}
                  </button>
                  <button
                    type="button"
                    onClick={handleConfirmClick}
                    disabled={isLoading}
                    className="min-w-[180px] h-12 px-8 rounded-full bg-green-500 text-white text-base font-bold transition-all hover:brightness-110 shadow-lg shadow-green-500/20 disabled:opacity-50"
                  >
                    {isLoading ? t("modal_paid_processing") : t("modal_paid_confirm")}
                  </button>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};
